import React, { useEffect, useState } from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../modules';
import {
  getPVPerSessionAsync,
  getSessionDurationPerSessionAsync,
  getSessionPerUVAsync,
  getTotalNewUserStatisticsAsync,
  getTotalPageViewStatisticsAsync,
  getTotalSessionStatisticsAsync,
  getTotalUserStatisticsAsync,
} from '../../../modules/serviceAnalytics/dashboard';
import {
  GetDashboardStatisticsParamType,
  GetPVMeanStatisticsParamType,
  GetUsageMeanStatisticsParamType,
  GetUVMeanStatisticsParamType,
} from '../../../api/serviceAnalytics/dashboard/type';

// components

import {
  ContentsLayout,
  ContainerGrid,
  ItemGrid,
} from '../../../components/UIComponents/Layout/DefaultLayout';

import Header from '../../../components/Organisms/Header';
import TabButton from '../../../components/Molecule/TabButton';
import DashboardDefaultCard from '../../../components/Organisms/Card/DashboardDefaultCard';
import DashboardLineCard from '../../../components/Organisms/Card/DashboardLineCard';

const ServiceDashboard = () => {
  // redux
  const {
    isGetTUSLoading,
    isGetTNUSLoading,
    isGetTPVSLoading,
    isGetTSSLoading,

    isGetSPULoading,
    isGetPPSLoading,
    isGetSDPSLoading,

    TUSData,
    TNUSData,
    TPVSData,
    TSSData,

    SPUData,
    PPSData,
    SDPSData,
  } = useSelector((state: RootState) => state.serviceDashboard);
  const dispatch = useDispatch();

  const onGetTotalStatistics = (info: GetDashboardStatisticsParamType) => {
    dispatch(getTotalUserStatisticsAsync.request(info));
    dispatch(getTotalNewUserStatisticsAsync.request(info));
    dispatch(getTotalPageViewStatisticsAsync.request(info));
    dispatch(getTotalSessionStatisticsAsync.request(info));
  };

  const onGetPVPerSession = (info: GetPVMeanStatisticsParamType) => {
    dispatch(getPVPerSessionAsync.request(info));
  };
  const onGetSessionPerUV = (info: GetUVMeanStatisticsParamType) => {
    dispatch(getSessionPerUVAsync.request(info));
  };
  const onGetSessionDurationPerSession = (
    info: GetUsageMeanStatisticsParamType
  ) => {
    dispatch(getSessionDurationPerSessionAsync.request(info));
  };

  // state

  const [activeTabID, setActiveTabID] = useState(0);

  const [activeDurationName, setActiveDurationName] = useState('day');

  const onGetDataDashboard = (unit: string) => {
    const info = {
      unit,
    };
    onGetTotalStatistics(info);
    onGetPVPerSession(info);
    onGetSessionPerUV(info);
    onGetSessionDurationPerSession(info);
  };

  useEffect(() => {
    onGetDataDashboard('day');
  }, []);

  const onClickTab = (id: number) => {
    setActiveTabID(id);
    switch (id) {
      case 0:
        onGetDataDashboard('day');
        setActiveDurationName('day');
        break;
      case 1:
        onGetDataDashboard('week');
        setActiveDurationName('week');
        break;
      case 2:
        onGetDataDashboard('month');
        setActiveDurationName('month');
        break;
      default:
        onGetDataDashboard('day');
        setActiveDurationName('day');
        break;
    }
  };

  return (
    <ContentsLayout>
      <Header title="서비스 이용 분석" isDatePicker={false}>
        <TabButton
          buttonData={[
            {
              id: 0,
              title: '어제',
            },
            {
              id: 1,
              title: '지난주',
            },
            {
              id: 2,
              title: '지난달',
            },
          ]}
          activeID={activeTabID}
          onClickTab={onClickTab}
        />
      </Header>

      <ContainerGrid $spacing="xsm">
        <ItemGrid $desktop={3}>
          <DashboardDefaultCard
            titleEng="Unique Visitor"
            titleKor="전체 사용자 수"
            rate={TUSData ? TUSData.rate : undefined}
            value={TUSData ? TUSData.count : undefined}
            isLoading={isGetTUSLoading}
            isTimeCount={false}
          />
        </ItemGrid>
        <ItemGrid $desktop={3}>
          <DashboardDefaultCard
            titleEng="New Visitor"
            titleKor="신규 사용자 수"
            rate={TNUSData ? TNUSData.rate : undefined}
            value={TNUSData ? TNUSData.count : undefined}
            isLoading={isGetTNUSLoading}
            isTimeCount={false}
          />
        </ItemGrid>
        <ItemGrid $desktop={3}>
          <DashboardDefaultCard
            titleEng="Page View"
            titleKor="페이지뷰 수"
            rate={TPVSData ? TPVSData.rate : undefined}
            value={TPVSData ? TPVSData.count : undefined}
            isLoading={isGetTPVSLoading}
            isTimeCount={false}
          />
        </ItemGrid>
        <ItemGrid $desktop={3}>
          <DashboardDefaultCard
            titleEng="Session"
            titleKor="방문 횟수"
            rate={TSSData ? TSSData.rate : undefined}
            value={TSSData ? TSSData.count : undefined}
            isLoading={isGetTSSLoading}
            isTimeCount={false}
          />
        </ItemGrid>

        <ItemGrid $desktop={4}>
          <DashboardLineCard
            titleEng="PV per Session"
            titleKor="1회 방문당 평균 페이지뷰 수"
            rate={PPSData && PPSData.rate ? PPSData.rate : undefined}
            value={PPSData ? PPSData.total : undefined}
            isLoading={isGetPPSLoading}
            data={PPSData ? PPSData.graph : undefined}
            lineChartName="페이지 뷰"
            option={activeDurationName}
            isTimeCount={false}
          />
        </ItemGrid>

        <ItemGrid $desktop={4}>
          <DashboardLineCard
            titleEng="SESSION PER UV"
            titleKor="1인당 평균 방문 횟수"
            data={SPUData ? SPUData.graph : undefined}
            lineChartName="페이지 뷰"
            isTimeCount={false}
            isLoading={isGetSPULoading}
            option={activeDurationName}
            rate={SPUData && SPUData.rate ? SPUData.rate : undefined}
            value={SPUData ? SPUData.total : undefined}
          />
        </ItemGrid>

        <ItemGrid $desktop={4}>
          <DashboardLineCard
            titleEng="Session Duration per Session"
            titleKor="1회 방문당 평균 서비스 이용 시간"
            rate={SDPSData && SDPSData.rate ? SDPSData.rate : undefined}
            time={SDPSData ? SDPSData.total : undefined}
            isLoading={isGetSDPSLoading}
            data={SDPSData ? SDPSData.graph : undefined}
            lineChartName="이용 시간"
            option={activeDurationName}
            isTimeCount
          />
        </ItemGrid>
      </ContainerGrid>
    </ContentsLayout>
  );
};

export default ServiceDashboard;
